import { styled } from "goober";
import { useEffect, useRef } from "react";
import { Icon } from "../Icon";
import { PulsingIcon } from "../PulsingIcon";
import { ShimmerSpan } from "../ShimmerSpan";
import { Text } from "../Text";

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 28px;
  padding: 6px 10px 6px 8px;
`;

const JIGGLE: Keyframe[] = [
  { transform: "translateX(0)" },
  { transform: "translateX(-3px)" },
  { transform: "translateX(3px)" },
  { transform: "translateX(-2px)" },
  { transform: "translateX(2px)" },
  { transform: "translateX(0)" },
];

export function PushLiveRequestedNote({
  approvedBy,
}: {
  approvedBy: string | null;
}) {
  const rowRef = useRef<HTMLDivElement>(null);

  // Clicking into the composer is the moment someone is about to make a change that cancels the request.
  useEffect(() => {
    const row = rowRef.current;
    const chat = row?.closest("[data-chat]");
    if (!row || !chat) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onPointerDown = (event: Event) => {
      if (reducedMotion.matches) return;
      if (!(event.target as Element).closest("[data-composer]")) return;
      row.animate(JIGGLE, { duration: 400, easing: "ease-in-out" });
    };
    chat.addEventListener("pointerdown", onPointerDown);
    return () => chat.removeEventListener("pointerdown", onPointerDown);
  }, []);

  return (
    <Row ref={rowRef}>
      <PulsingIcon data-pending={!approvedBy}>
        <Icon
          name={approvedBy ? "check-filled" : "heart-handshake"}
          size={12}
          color={approvedBy ? "var(--green-600)" : "var(--text-secondary)"}
        />
      </PulsingIcon>
      <Text variant="text-small" color="var(--text-secondary)">
        <ShimmerSpan data-pending={!approvedBy}>
          <Text variant="text-small-md" color="var(--text-primary)">
            {approvedBy ? `Approved by ${approvedBy}.` : "Push request sent."}
          </Text>{" "}
          {approvedBy
            ? "Push live when you’re ready. Making more changes will cancel the approval."
            : "Making more changes will cancel it."}
        </ShimmerSpan>
      </Text>
    </Row>
  );
}
